import React, { useEffect, useState } from "react";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "../../services/api/notificationService";

const TYPE_LABELS = {
  challenge: { icon: "⚔️", label: "Match challenges" },
  invite: { icon: "👥", label: "Team / tournament / league invites" },
  friend_request: { icon: "🙋", label: "Friend requests" },
  general_alert: { icon: "📢", label: "General alerts" },
  match_result: { icon: "🏁", label: "Match results" },
  team: { icon: "🛡️", label: "Team join / membership updates" },
  direct_message: { icon: "💬", label: "Direct messages" },
  follower: { icon: "⭐", label: "New followers" },
  league: { icon: "🏆", label: "League chat messages" },
};

/**
 * Notification Preferences modal — lets a user enable/disable push
 * (real-time in-app + browser notification) delivery, notification sound,
 * email delivery (default OFF — actual sending is parked for later), and
 * per-event-type toggles.
 */
const NotificationPreferencesModal = ({ isOpen, onClose }) => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [browserPermission, setBrowserPermission] = useState(
    typeof window !== "undefined" && window.Notification ? window.Notification.permission : "unsupported"
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    getNotificationPreferences()
      .then((res) => {
        if (!cancelled) setPrefs(res.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load your notification preferences.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const save = async (updates) => {
    const next = {
      ...prefs,
      ...updates,
      types: { ...prefs.types, ...(updates.types || {}) },
    };
    setPrefs(next); // optimistic
    setSaving(true);
    setError("");
    try {
      const res = await updateNotificationPreferences(updates);
      setPrefs(res.data?.data || next);
    } catch {
      setError("Couldn't save your change. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const togglePush = async () => {
    const enabling = !prefs.push;
    if (enabling && typeof window !== "undefined" && window.Notification && window.Notification.permission === "default") {
      try {
        const result = await window.Notification.requestPermission();
        setBrowserPermission(result);
      } catch {
        // ignore — user can still enable in-app push without browser popups
      }
    }
    save({ push: enabling });
  };

  return (
    <div className="upgrade-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="upgrade-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="upgrade-modal-header">
          <div>
            <h3>🔔 Notification Settings</h3>
            <p>Choose how and when Sportifide notifies you.</p>
          </div>
          <button className="upgrade-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading && <p style={{ color: "rgba(255,255,255,0.6)" }}>Loading…</p>}
        {error && <p style={{ color: "#f87171", fontSize: "0.85rem" }}>{error}</p>}

        {prefs && (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "1rem" }}>
            <label className="notif-pref-row">
              <div>
                <div className="notif-pref-title">🔔 Push notifications</div>
                <div className="notif-pref-sub">
                  Real-time in-app alerts{browserPermission !== "unsupported" ? " + browser pop-ups" : ""}.
                  {browserPermission === "denied" && prefs.push && (
                    <span style={{ color: "#f59e0b" }}> Browser permission blocked — enable it in your browser settings.</span>
                  )}
                </div>
              </div>
              <input type="checkbox" checked={!!prefs.push} disabled={saving} onChange={togglePush} />
            </label>

            <label className="notif-pref-row">
              <div>
                <div className="notif-pref-title">🔊 Notification sound</div>
                <div className="notif-pref-sub">Play a short chime when a new notification arrives.</div>
              </div>
              <input
                type="checkbox"
                checked={!!prefs.sound}
                disabled={saving}
                onChange={() => save({ sound: !prefs.sound })}
              />
            </label>

            <label className="notif-pref-row">
              <div>
                <div className="notif-pref-title">✉️ Email notifications</div>
                <div className="notif-pref-sub">
                  Off by default so your inbox isn't spammed. Email delivery is coming soon — this just saves your preference for later.
                </div>
              </div>
              <input
                type="checkbox"
                checked={!!prefs.email}
                disabled={saving}
                onChange={() => save({ email: !prefs.email })}
              />
            </label>

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "10px" }}>
              <div className="notif-pref-title" style={{ marginBottom: "8px" }}>What to notify me about</div>
              {Object.entries(TYPE_LABELS).map(([key, { icon, label }]) => (
                <label className="notif-pref-row" key={key}>
                  <div>
                    <div className="notif-pref-title" style={{ fontSize: "0.85rem" }}>{icon} {label}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={prefs.types?.[key] !== false}
                    disabled={saving}
                    onChange={() => save({ types: { [key]: !(prefs.types?.[key] !== false) } })}
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="upgrade-modal-actions" style={{ marginTop: "1.5rem" }}>
          <button className="upgrade-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferencesModal;
